import React, { useState, useEffect } from "react";
import { database } from "../../firebaseConfig";
import { ref, set, get, push } from "firebase/database";
import "./TeamSettings.css";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebaseConfig";

const TeamSettings = ({ userId }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentMember, setCurrentMember] = useState({
    name: "",
    role: "",
    companyName: "",
    companySlogan: "",
    personalEmail: "",
    generalEmail: "",
    phone: "",
    website: "",
    // Rechnungsanschrift
    billingStreet: "",
    billingZip: "",
    billingCity: "",
    billingCountry: "",
    vatId: "",
    taxNumber: "",
    tradeRegister: "",
    ceo: "",
    // Banking
    bankName: "",
    iban: "",
    bic: "",
    signature: "",
    signatureMaxWidth: 150
  });
  const [companySettings, setCompanySettings] = useState({
    defaultCompanyName: "BonusClub.io"
  });
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [signatureFile, setSignatureFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Load existing team members and company settings
  useEffect(() => {
    const loadTeamData = async () => {
      if (!userId) return;

      try {
        // Load team members
        const teamRef = ref(database, `/World/${userId}/team/members`);
        const snapshot = await get(teamRef);

        if (snapshot.exists()) {
          const members = [];
          snapshot.forEach((childSnapshot) => {
            members.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setTeamMembers(members);
        }

        // Load company settings
        const settingsRef = ref(database, `/World/${userId}/team/settings`);
        const settingsSnapshot = await get(settingsRef);

        if (settingsSnapshot.exists()) {
          const settings = settingsSnapshot.val();
          setCompanySettings({
            defaultCompanyName: settings.defaultCompanyName || "BonusClub.io"
          });
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading team data:", error);
        showNotification("Error loading team data", "error");
        setIsLoading(false);
      }
    };

    loadTeamData();
  }, [userId]);

  // Trigger resync of public offers when team member is updated
  const triggerPublicOffersResyncForTeamMember = async (teamMemberId) => {
    try {
      // Get all offers
      const offersRef = ref(database, `/World/${userId}/offers`);
      const offersSnapshot = await get(offersRef);
      
      if (!offersSnapshot.exists()) return;
      
      const offers = offersSnapshot.val();
      
      // Find and resync public offers that use this team member
      for (const [offerId, offerData] of Object.entries(offers)) {
        if (offerData.isPublic && offerData.teamMemberId === teamMemberId) {
          await resyncPublicOfferTeamData(offerId, offerData);
        }
      }
      
      console.log(`✅ Resynced public offers after team member ${teamMemberId} update`);
    } catch (error) {
      console.error("Error resyncing public offers after team member update:", error);
    }
  };

  // Resync public offer team data
  const resyncPublicOfferTeamData = async (offerId, offerData) => {
    try {
      const customId = `${userId}+${offerId}`;
      
      // Get updated team member data
      let teamMemberData = null;
      if (offerData.teamMemberId) {
        const teamMemberRef = ref(database, `/World/${userId}/team/members/${offerData.teamMemberId}`);
        const teamMemberSnapshot = await get(teamMemberRef);
        if (teamMemberSnapshot.exists()) {
          teamMemberData = teamMemberSnapshot.val();
        }
      }
      
      // Get current public offer data and update only team member part
      const publicOfferRef = ref(database, `/publicOffers/${customId}`);
      const publicOfferSnapshot = await get(publicOfferRef);
      
      if (publicOfferSnapshot.exists()) {
        const currentPublicOffer = publicOfferSnapshot.val();
        const updatedPublicOffer = {
          ...currentPublicOffer,
          teamMember: teamMemberData,
          lastUpdated: Date.now()
        };
        
        await set(publicOfferRef, updatedPublicOffer);
      }
    } catch (error) {
      console.error(`Error resyncing team data for public offer ${offerId}:`, error);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentMember(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle company settings changes
  const handleCompanySettingsChange = (e) => {
    const { name, value } = e.target;
    setCompanySettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save company settings
  const saveCompanySettings = async () => {
    if (!userId) return;

    try {
      const settingsRef = ref(database, `/World/${userId}/team/settings`);
      await set(settingsRef, companySettings);
      showNotification("Company settings successfully saved", "success");
    } catch (error) {
      console.error("Error saving company settings:", error);
      showNotification("Error saving company settings", "error");
    }
  };

  // Edit member
  const handleEditMember = (member) => {
    setCurrentMember(member);
    setEditingId(member.id);
  };

  // Delete member
  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to delete this team member?")) return;

    try {
      const memberRef = ref(database, `/World/${userId}/team/members/${memberId}`);
      await set(memberRef, null);
      
      setTeamMembers(prev => prev.filter(member => member.id !== memberId));
      showNotification("Team member deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting team member:", error);
      showNotification("Error deleting team member", "error");
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!currentMember.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!currentMember.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }
    
    if (!currentMember.companySlogan.trim()) {
      newErrors.companySlogan = "Company slogan is required";
    }
    
    if (!currentMember.personalEmail.trim()) {
      newErrors.personalEmail = "Personal email is required";
    } else if (!/\S+@\S+\.\S+/.test(currentMember.personalEmail)) {
      newErrors.personalEmail = "Please enter a valid email address";
    }
    
    if (!currentMember.generalEmail.trim()) {
      newErrors.generalEmail = "General email is required";
    } else if (!/\S+@\S+\.\S+/.test(currentMember.generalEmail)) {
      newErrors.generalEmail = "Please enter a valid email address";
    }
    
    if (!currentMember.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    
    if (!currentMember.website.trim()) {
      newErrors.website = "Website is required";
    } else if (!/^https?:\/\/.*/.test(currentMember.website)) {
      newErrors.website = "Please enter a valid URL starting with http:// or https://";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle signature file change
  const handleSignatureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.match('image.*')) {
      showNotification("Please select an image file for the signature (PNG with transparency recommended)", "error");
      return;
    }
    
    if (file.size > 1024 * 1024) { // 1MB limit
      showNotification("The image file is too large (max. 1MB)", "error");
      return;
    }
    
    setSignatureFile(file);
    
    // Vorschau anzeigen
    const reader = new FileReader();
    reader.onload = (event) => {
      setCurrentMember(prev => ({
        ...prev,
        signature: event.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  // Handle signature width change
  const handleSignatureWidthChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 50 && value <= 400) {
      const roundedValue = Math.round(value / 5) * 5;
      setCurrentMember(prev => ({
        ...prev,
        signatureMaxWidth: roundedValue
      }));
    }
  };

  // Remove signature
  const handleRemoveSignature = () => {
    setSignatureFile(null);
    setCurrentMember(prev => ({
      ...prev,
      signature: "",
      signatureMaxWidth: 150
    }));
  };

  // Handle save with validation and signature upload
  const handleSave = async () => {
    if (!userId) return;

    if (!validateForm()) {
      showNotification("Please fill in all required fields correctly", "error");
      return;
    }

    setIsUploading(true);

    try {
      let signatureUrl = currentMember.signature;

      // Upload signature if new file is selected
      if (signatureFile) {
        const timestamp = new Date().getTime();
        const fileExtension = signatureFile.name.split('.').pop();
        const fileName = `signatures/${timestamp}.${fileExtension}`;
        const fileRef = storageRef(storage, fileName);
        
        await uploadBytes(fileRef, signatureFile);
        signatureUrl = await getDownloadURL(fileRef);
      }

      let memberRef;
      const memberData = {
        ...currentMember,
        signature: signatureUrl,
        updatedAt: Date.now()
      };

      if (editingId) {
        memberRef = ref(database, `/World/${userId}/team/members/${editingId}`);
        await set(memberRef, memberData);

        setTeamMembers(prev => prev.map(member => 
          member.id === editingId ? { ...memberData, id: editingId } : member
        ));
      } else {
        memberRef = ref(database, `/World/${userId}/team/members`);
        const newMemberRef = push(memberRef);
        await set(newMemberRef, {
          ...memberData,
          createdAt: Date.now()
        });

        const newMember = {
          id: newMemberRef.key,
          ...memberData,
          createdAt: Date.now()
        };
        setTeamMembers(prev => [...prev, newMember]);
      }

      // Reset form
      setCurrentMember({
        name: "",
        role: "",
        companyName: "",
        companySlogan: "",
        personalEmail: "",
        generalEmail: "",
        phone: "",
        website: "",
        billingStreet: "",
        billingZip: "",
        billingCity: "",
        billingCountry: "",
        vatId: "",
        taxNumber: "",
        tradeRegister: "",
        ceo: "",
        bankName: "",
        iban: "",
        bic: "",
        signature: "",
        signatureMaxWidth: 150
      });
      setSignatureFile(null);
      setErrors({});
      setEditingId(null);
      showNotification(
        editingId ? "Team member successfully updated" : "New team member successfully added", 
        "success"
      );
      
      // If this was an update, resync public offers that use this team member
      if (editingId) {
        triggerPublicOffersResyncForTeamMember(editingId);
      }
    } catch (error) {
      console.error("Error saving team member:", error);
      showNotification("Error saving team member", "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Show notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  if (isLoading) {
    return (
      <div className="offer-maker__loading">
        <div className="offer-maker__spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="offer-maker__editor">
      {notification.message && (
        <div className={`offer-maker__notification offer-maker__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Company Settings */}
      <div className="offer-maker__section">
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">🏢</span>
          <h3 className="offer-maker__section-title">Company Settings</h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Default Company Name
            <span className="offer-maker__input-hint">
              This name will be used when no team member is assigned to an offer
            </span>
          </label>
          <input
            type="text"
            name="defaultCompanyName"
            value={companySettings.defaultCompanyName}
            onChange={handleCompanySettingsChange}
            className="offer-maker__input"
            placeholder="e.g. Your Company Name"
          />
        </div>

        <div className="offer-maker__button-group">
          <button 
            type="button"
            onClick={saveCompanySettings}
            className="offer-maker__save-btn"
          >
            Save Company Settings
          </button>
        </div>
      </div>

      {/* Team Members List */}
      <div className="offer-maker__section">
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">👥</span>
          <h3 className="offer-maker__section-title">Team Members</h3>
        </div>

        <div className="offer-maker__team-list">
          {teamMembers.map(member => (
            <div key={member.id} className="offer-maker__team-member-card">
              <div className="offer-maker__team-member-info">
                <h4>{member.name}</h4>
                <p>{member.companyName}</p>
                <p>{member.personalEmail}</p>
              </div>
              <div className="offer-maker__team-member-actions">
                <button 
                  className="offer-maker__edit-btn"
                  onClick={() => handleEditMember(member)}
                >
                  Edit
                </button>
                <button 
                  className="offer-maker__delete-btn"
                  onClick={() => handleDeleteMember(member.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Form */}
      <div className="offer-maker__section">
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">{editingId ? '✏️' : '➕'}</span>
          <h3 className="offer-maker__section-title">
            {editingId ? 'Edit Team Member' : 'Add New Team Member'}
          </h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Name <span className="offer-maker__required">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={currentMember.name}
            onChange={handleInputChange}
            className={`offer-maker__input ${errors.name ? 'offer-maker__input--error' : ''}`}
            placeholder="Your full name"
          />
          {errors.name && <span className="offer-maker__error-message">{errors.name}</span>}
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Role / Function
          </label>
          <input
            type="text"
            name="role"
            value={currentMember.role || ""}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="e.g. CEO, Geschäftsführer, Founder"
          />
          <small className="offer-maker__input-hint">
            Used in the signature section of offers (under the name).
          </small>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Company Name <span className="offer-maker__required">*</span>
          </label>
          <input
            type="text"
            name="companyName"
            value={currentMember.companyName}
            onChange={handleInputChange}
            className={`offer-maker__input ${errors.companyName ? 'offer-maker__input--error' : ''}`}
            placeholder="Your company name"
          />
          {errors.companyName && <span className="offer-maker__error-message">{errors.companyName}</span>}
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Company Slogan <span className="offer-maker__required">*</span>
          </label>
          <input
            type="text"
            name="companySlogan"
            value={currentMember.companySlogan}
            onChange={handleInputChange}
            className={`offer-maker__input ${errors.companySlogan ? 'offer-maker__input--error' : ''}`}
            placeholder="Your company slogan"
          />
          {errors.companySlogan && <span className="offer-maker__error-message">{errors.companySlogan}</span>}
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Personal Email <span className="offer-maker__required">*</span>
          </label>
          <input
            type="email"
            name="personalEmail"
            value={currentMember.personalEmail}
            onChange={handleInputChange}
            className={`offer-maker__input ${errors.personalEmail ? 'offer-maker__input--error' : ''}`}
            placeholder="your.name@company.com"
          />
          {errors.personalEmail && <span className="offer-maker__error-message">{errors.personalEmail}</span>}
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            General Email <span className="offer-maker__required">*</span>
          </label>
          <input
            type="email"
            name="generalEmail"
            value={currentMember.generalEmail}
            onChange={handleInputChange}
            className={`offer-maker__input ${errors.generalEmail ? 'offer-maker__input--error' : ''}`}
            placeholder="contact@company.com"
          />
          {errors.generalEmail && <span className="offer-maker__error-message">{errors.generalEmail}</span>}
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Phone <span className="offer-maker__required">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={currentMember.phone}
            onChange={handleInputChange}
            className={`offer-maker__input ${errors.phone ? 'offer-maker__input--error' : ''}`}
            placeholder="+1234567890"
          />
          {errors.phone && <span className="offer-maker__error-message">{errors.phone}</span>}
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Website <span className="offer-maker__required">*</span>
          </label>
          <input
            type="url"
            name="website"
            value={currentMember.website}
            onChange={handleInputChange}
            className={`offer-maker__input ${errors.website ? 'offer-maker__input--error' : ''}`}
            placeholder="https://www.company.com"
          />
          {errors.website && <span className="offer-maker__error-message">{errors.website}</span>}
        </div>

        {/* Rechnungsanschrift Section */}
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">📍</span>
          <h3 className="offer-maker__section-title">Invoice Address (Important for Invoices!)</h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Street & Number</label>
          <input
            type="text"
            name="billingStreet"
            value={currentMember.billingStreet}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="Musterstraße 123"
          />
        </div>

        <div className="offer-maker__input-group" style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: '1' }}>
            <label className="offer-maker__label">ZIP Code</label>
            <input
              type="text"
              name="billingZip"
              value={currentMember.billingZip}
              onChange={handleInputChange}
              className="offer-maker__input"
              placeholder="12345"
            />
          </div>
          <div style={{ flex: '2' }}>
            <label className="offer-maker__label">City</label>
            <input
              type="text"
              name="billingCity"
              value={currentMember.billingCity}
              onChange={handleInputChange}
              className="offer-maker__input"
              placeholder="Berlin"
            />
          </div>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Country</label>
          <input
            type="text"
            name="billingCountry"
            value={currentMember.billingCountry}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="Germany"
          />
        </div>

        {/* Tax Information */}
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">📋</span>
          <h3 className="offer-maker__section-title">Tax & Legal Information</h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            VAT ID (Umsatzsteuer-ID)
            <span className="offer-maker__input-hint">
              e.g. DE123456789 - Important for international invoices
            </span>
          </label>
          <input
            type="text"
            name="vatId"
            value={currentMember.vatId}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="DE123456789"
          />
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Tax Number (Steuernummer)
            <span className="offer-maker__input-hint">
              Optional - Required for domestic invoices in some countries
            </span>
          </label>
          <input
            type="text"
            name="taxNumber"
            value={currentMember.taxNumber}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="12/345/67890"
          />
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Trade Register (Handelsregister)
            <span className="offer-maker__input-hint">
              e.g. HRB 12345 - Required for GmbH/AG companies
            </span>
          </label>
          <input
            type="text"
            name="tradeRegister"
            value={currentMember.tradeRegister}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="HRB 12345"
          />
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            CEO / Managing Director (Geschäftsführer)
            <span className="offer-maker__input-hint">
              Required for GmbH companies
            </span>
          </label>
          <input
            type="text"
            name="ceo"
            value={currentMember.ceo}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="Max Mustermann"
          />
        </div>

        {/* Add signature upload section before banking details */}
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">✍️</span>
          <h3 className="offer-maker__section-title">Signature</h3>
        </div>

        <div className="offer-maker__signature-upload">
          <input
            type="file"
            accept="image/*"
            onChange={handleSignatureChange}
            className="offer-maker__signature-input"
            disabled={isUploading}
            id="signature-upload"
          />
          
          <div className="offer-maker__signature-settings">
            <label className="offer-maker__signature-width-label">
              Maximum width (px):
              <div className="offer-maker__signature-width-controls">
                <input
                  type="range"
                  min="50"
                  max="400"
                  step="5"
                  value={currentMember.signatureMaxWidth}
                  onChange={(e) => handleSignatureWidthChange(e)}
                  className="offer-maker__signature-width-slider"
                  style={{ '--slider-value': `${((currentMember.signatureMaxWidth - 50) / 350) * 100}%` }}
                />
                <input
                  type="number"
                  min="50"
                  max="400"
                  step="5"
                  value={currentMember.signatureMaxWidth}
                  onChange={(e) => handleSignatureWidthChange(e)}
                  className="offer-maker__signature-width-input"
                />
              </div>
            </label>
          </div>
          
          {currentMember.signature ? (
            <div className="offer-maker__signature-preview">
              <img 
                src={currentMember.signature} 
                alt="Signature preview" 
                style={{ maxWidth: `${currentMember.signatureMaxWidth}px` }}
              />
              <button
                type="button"
                className="offer-maker__signature-remove"
                onClick={handleRemoveSignature}
                title="Remove signature"
              >
                ×
              </button>
            </div>
          ) : (
            <label htmlFor="signature-upload" className="offer-maker__signature-placeholder">
              <span className="offer-maker__signature-icon">✍️</span>
              <span className="offer-maker__signature-text">
                Click here to upload a signature (PNG with transparency recommended)
              </span>
            </label>
          )}
        </div>

        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">🏦</span>
          <h3 className="offer-maker__section-title">Banking Details (Optional)</h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Bank Name</label>
          <input
            type="text"
            name="bankName"
            value={currentMember.bankName}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="Bank name"
          />
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">IBAN</label>
          <input
            type="text"
            name="iban"
            value={currentMember.iban}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="DE89 3704 0044 0532 0130 00"
          />
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">BIC</label>
          <input
            type="text"
            name="bic"
            value={currentMember.bic}
            onChange={handleInputChange}
            className="offer-maker__input"
            placeholder="DEUTDEDBXXX"
          />
        </div>
      </div>

      <div className="offer-maker__button-group">
        {editingId && (
          <button 
            className="offer-maker__cancel-btn" 
            onClick={() => {
              setCurrentMember({
                name: "",
                role: "",
                companyName: "",
                companySlogan: "",
                personalEmail: "",
                generalEmail: "",
                phone: "",
                website: "",
                billingStreet: "",
                billingZip: "",
                billingCity: "",
                billingCountry: "",
                vatId: "",
                taxNumber: "",
                tradeRegister: "",
                ceo: "",
                bankName: "",
                iban: "",
                bic: "",
                signature: "",
                signatureMaxWidth: 150
              });
              setErrors({});
              setEditingId(null);
            }}
          >
            Cancel Edit
          </button>
        )}
        <button 
          className="offer-maker__save-btn" 
          onClick={handleSave}
        >
          {editingId ? 'Update' : 'Add'} Team Member
          <span className="offer-maker__save-icon">💾</span>
        </button>
      </div>
    </div>
  );
};

const TeamMemberForm = ({ member, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: member?.name || '',
    companyName: member?.companyName || '',
    companySlogan: member?.companySlogan || '',
    personalEmail: member?.personalEmail || '',
    generalEmail: member?.generalEmail || '',
    phone: member?.phone || '',
    website: member?.website || '',
    bankName: member?.bankName || '',
    iban: member?.iban || '',
    bic: member?.bic || '',
    signature: member?.signature || '',
    signatureMaxWidth: member?.signatureMaxWidth || 150
  });
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(member?.signature || '');
  const [isUploading, setIsUploading] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });

  // Show notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  const handleSignatureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.match('image.*')) {
      showNotification("Please select an image file for the signature (PNG with transparency recommended)", "error");
      return;
    }
    
    if (file.size > 1024 * 1024) { // 1MB limit
      showNotification("The image file is too large (max. 1MB)", "error");
      return;
    }
    
    setSignatureFile(file);
    
    // Vorschau anzeigen
    const reader = new FileReader();
    reader.onload = (event) => {
      setSignaturePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      let signatureUrl = formData.signature;

      if (signatureFile) {
        const timestamp = new Date().getTime();
        const fileExtension = signatureFile.name.split('.').pop();
        const fileName = `signatures/${timestamp}.${fileExtension}`;
        const fileRef = storageRef(storage, fileName);
        
        await uploadBytes(fileRef, signatureFile);
        signatureUrl = await getDownloadURL(fileRef);
      }

      const updatedData = {
        ...formData,
        signature: signatureUrl
      };

      onSave(updatedData);
    } catch (error) {
      console.error("Error uploading signature:", error);
      showNotification("Die Signatur konnte nicht hochgeladen werden", "error");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="team-settings__form">
      {/* ... existing form fields ... */}
      
      <div className="team-settings__form-section">
        <h4 className="team-settings__form-section-title">Signature</h4>
        
        <div className="team-settings__signature-upload">
          <input
            type="file"
            accept="image/*"
            onChange={handleSignatureChange}
            className="team-settings__signature-input"
            disabled={isUploading}
            id="signature-upload"
          />
          
          <div className="team-settings__signature-settings">
            <label className="team-settings__signature-width-label">
              Maximum width (px):
              <input
                type="number"
                min="50"
                max="400"
                value={formData.signatureMaxWidth}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  signatureMaxWidth: Math.max(50, Math.min(400, parseInt(e.target.value) || 150))
                }))}
                className="team-settings__signature-width-input"
              />
            </label>
          </div>
          
          {signaturePreview ? (
            <div className="team-settings__signature-preview">
              <img 
                src={signaturePreview} 
                alt="Signature preview" 
                style={{ maxWidth: `${formData.signatureMaxWidth}px` }}
              />
              <button
                type="button"
                className="team-settings__signature-remove"
                onClick={() => {
                  setSignatureFile(null);
                  setSignaturePreview('');
                  setFormData(prev => ({ ...prev, signature: '' }));
                }}
                title="Remove signature"
              >
                ×
              </button>
            </div>
          ) : (
            <label htmlFor="signature-upload" className="team-settings__signature-placeholder">
              <span className="team-settings__signature-icon">✍️</span>
              <span className="team-settings__signature-text">
                Click here to upload a signature (PNG with transparency recommended)
              </span>
            </label>
          )}
        </div>
      </div>

      <div className="team-settings__form-actions">
        <button 
          type="button" 
          onClick={onCancel}
          className="team-settings__cancel-btn"
          disabled={isUploading}
        >
          Abbrechen
        </button>
        <button 
          type="submit" 
          className="team-settings__save-btn"
          disabled={isUploading}
        >
          {isUploading ? "Speichern..." : "Speichern"}
        </button>
      </div>
    </form>
  );
};

export default TeamSettings; 